import { useState } from 'react';
import { Plus, Trash2, CalendarDays } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getJsLocale } from '../utils/dateLocale';

export const SpecialOpeningEditor = ({ specialDates = [], onChange }) => {
  const { t, language } = useLanguage();
  const [showAdd, setShowAdd] = useState(false);
  const [newDate, setNewDate] = useState('');
  const [newFrom, setNewFrom] = useState('18:00');
  const [newTo, setNewTo] = useState('23:00');
  const [newDesc, setNewDesc] = useState('');

  const dates = Array.isArray(specialDates) ? specialDates : [];

  // Filter out past dates
  const today = new Date().toISOString().split('T')[0];
  const activeDates = dates.filter(d => d.date >= today);
  const pastDates = dates.filter(d => d.date < today);

  const addDate = () => {
    if (!newDate || !newFrom || !newTo) return;
    const entry = {
      id: Date.now().toString(),
      date: newDate,
      time_from: newFrom,
      time_to: newTo,
      description: newDesc.trim(),
    };
    onChange([...activeDates, entry]);
    setNewDate('');
    setNewFrom('18:00');
    setNewTo('23:00');
    setNewDesc('');
    setShowAdd(false);
  };

  const removeDate = (id) => {
    onChange(activeDates.filter(d => d.id !== id));
  };

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString(getJsLocale(language), { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-3" data-testid="special-opening-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-amber-400" />
          <span className="text-white font-semibold text-sm">Saerlige abningsdage</span>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(!showAdd)}
          className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1 px-3 py-1.5 rounded-lg hover:bg-emerald-500/10 transition-colors"
          data-testid="add-special-date-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          Tilfoej dato
        </button>
      </div>

      <div className="p-3 bg-zinc-800/50 border border-white/10 rounded-xl">
        <p className="text-white/60 text-xs">
          {t('gen.845c8045d7')}
        </p>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="p-4 bg-zinc-900 border border-emerald-500/20 rounded-xl space-y-3" data-testid="special-date-form">
          <div>
            <label className="text-xs text-zinc-400 block mb-1">Dato</label>
            <input
              type="date"
              value={newDate}
              min={today}
              onChange={(e) => setNewDate(e.target.value)}
              className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2.5 text-white text-sm"
              data-testid="special-date-input"
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-zinc-400 block mb-1">{t('gen.6b8781ae95')}</label>
              <input
                type="time"
                value={newFrom}
                onChange={(e) => setNewFrom(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2.5 text-white text-sm"
                data-testid="special-time-from"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-zinc-400 block mb-1">{t('gen.cd01618bee')}</label>
              <input
                type="time"
                value={newTo}
                onChange={(e) => setNewTo(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2.5 text-white text-sm"
                data-testid="special-time-to"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400 block mb-1">{t('register.descriptionLabel')}</label>
            <input
              type="text"
              value={newDesc}
              onChange={(e) => setNewDesc(e.target.value)}
              placeholder={t('gen.e22ee281c4')}
              className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2.5 text-white text-sm placeholder:text-zinc-600"
              data-testid="special-desc-input"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={addDate}
              disabled={!newDate || !newFrom || !newTo}
              className="flex-1 py-2.5 rounded-lg bg-emerald-500 text-zinc-900 font-bold text-sm hover:bg-emerald-400 transition-colors disabled:opacity-40"
              data-testid="special-date-add-confirm"
            >
              Tilfoej
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              className="px-4 py-2.5 rounded-lg bg-zinc-800 text-zinc-400 border border-zinc-700 hover:text-white transition-colors"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {/* List of special dates */}
      {activeDates.length > 0 ? (
        <div className="space-y-1.5">
          {activeDates
            .sort((a, b) => a.date.localeCompare(b.date))
            .map((d) => (
              <div
                key={d.id}
                className="flex items-center gap-3 px-4 py-3 bg-zinc-800/40 border border-white/5 rounded-xl group"
                data-testid={`special-date-${d.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-amber-400 text-sm font-semibold">
                      {formatDate(d.date)}
                    </span>
                    <span className="text-emerald-400 text-sm font-mono">
                      {d.time_from}-{d.time_to}
                    </span>
                  </div>
                  {d.description && (
                    <p className="text-white/60 text-xs mt-0.5 truncate">{d.description}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => removeDate(d.id)}
                  className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors opacity-60 group-hover:opacity-100"
                  data-testid={`remove-special-${d.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
        </div>
      ) : (
        !showAdd && (
          <div className="text-center py-4 text-zinc-500 text-xs">
            Ingen saerlige abningsdage tilfojet endnu
          </div>
        )
      )}

      {/* Reminder to save */}
      {activeDates.length > 0 && (
        <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl">
          <p className="text-amber-400 text-xs text-center font-medium">
            {t('gen.c025966783')}
          </p>
        </div>
      )}
    </div>
  );
};

export default SpecialOpeningEditor;
